import { useTranslation } from 'react-i18next';
import { HelpCircle, Compass, BookOpen, Library, ExternalLink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getDocsUrl, getContextualDocsSlug } from '@/lib/docs';
import { APP_VERSION } from '@/lib/appVersion';

interface HelpMenuProps {
  /**
   * Start the role-scoped product tour (from `useOnboardingTour`). Absent —
   * no tour to offer (the unified dashboard has none), and no menu item.
   */
  onStartTour?: () => void;
  /**
   * Current route path — used to deep-link the "this section" docs item.
   */
  currentPath: string;
  buttonClassName?: string;
  iconClassName?: string;
}

/**
 * HelpMenu — the sidebar "?" control.
 *
 * Mirrors the language/theme controls: an icon button that opens a dropdown.
 * Offers three actions:
 *   1. Start the guided product tour.
 *   2. Open the docs section for the current page (new tab, context-aware).
 *   3. Open the full documentation home (new tab).
 *
 * Docs always open on the marketing site via {@link getDocsUrl} — never as a
 * relative app link that would 404 / leave the SPA.
 */
export function HelpMenu({
  onStartTour,
  currentPath,
  buttonClassName,
  iconClassName,
}: HelpMenuProps) {
  const { t, i18n } = useTranslation('dashboard');

  const openDocs = (slug?: string) => {
    window.open(getDocsUrl(slug, i18n.language), '_blank', 'noopener,noreferrer');
  };

  const sectionSlug = getContextualDocsSlug(currentPath);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={buttonClassName}
          aria-label={t('appSidebar.help', 'Помощь')}
          title={t('appSidebar.help', 'Помощь')}
        >
          <HelpCircle className={iconClassName} aria-hidden="true" />
          <span className="sr-only">{t('appSidebar.help', 'Помощь')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 p-1">
        <DropdownMenuLabel className="flex items-baseline justify-between gap-2">
          <span>{t('appSidebar.help', 'Помощь')}</span>
          {/* Injected from the latest CHANGELOG.md entry at build time; empty
              (hidden) in environments without the define, e.g. vitest. */}
          {APP_VERSION && (
            <span className="text-xs font-normal text-fg-subtle">v{APP_VERSION}</span>
          )}
        </DropdownMenuLabel>
        {onStartTour && (
          <>
            <DropdownMenuItem onClick={onStartTour} className="flex items-center gap-2.5">
              <Compass className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
              <span>{t('appSidebar.menuTour', 'Пройти тур')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {/* Context-aware: only when the current page maps to a docs section */}
        {sectionSlug && (
          <DropdownMenuItem
            onClick={() => openDocs(sectionSlug)}
            className="flex items-center justify-between gap-2.5"
          >
            <span className="flex items-center gap-2.5">
              <BookOpen className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
              <span>{t('appSidebar.menuDocsSection', 'Справка по этому разделу')}</span>
            </span>
            <ExternalLink
              className="h-3.5 w-3.5 shrink-0 text-fg-subtle"
              aria-hidden="true"
            />
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => openDocs()}
          className="flex items-center justify-between gap-2.5"
        >
          <span className="flex items-center gap-2.5">
            <Library className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
            <span>{t('appSidebar.menuDocsAll', 'Вся документация')}</span>
          </span>
          <ExternalLink
            className="h-3.5 w-3.5 shrink-0 text-fg-subtle"
            aria-hidden="true"
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
